/**
 * Compress an image file client-side before upload.
 * Resizes to max 1920px width/height and converts to JPEG at 0.85 quality.
 * Fills transparent areas with white background for PNG→JPEG conversion.
 * @param {File|Blob} file - Original image file
 * @param {number} maxSize - Max width/height in pixels (default 1920)
 * @returns {Promise<Blob>} Compressed image blob (JPEG)
 */
export function compressImage(file, maxSize = 1920) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      let { width, height } = img;
      
      // Safety check: valid dimensions
      if (!width || !height) {
        reject(new Error('Image has invalid dimensions'));
        return;
      }
      
      // Only downscale, never upscale
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Fill white background first (handles PNG transparency → JPEG)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob && blob.size > 0) {
            resolve(blob);
          } else {
            reject(new Error('Image compression produced empty result'));
          }
        },
        'image/jpeg',
        0.85
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };
    
    img.src = url;
  });
}
